import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import {
  LockClosedIcon,
  LockOpenIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import TagInput from '../bookmark/TagInput'
import FolderSelect from '../bookmark/FolderSelect'
import { bookmarkApi } from '../../../../shared/services/bookmarkApi'
import { Tag, BookmarkedUrl } from '@/types/bookmark'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '../../../../shared/lib/format'

interface EditUrlModalProps {
  url: BookmarkedUrl
  onClose: () => void
  onUpdate: (url: BookmarkedUrl) => void
}

/** 링크 편집 모달 (제목·메모·태그·폴더·비밀번호·만료일) */
const EditUrlModal: React.FC<EditUrlModalProps> = ({
  url,
  onClose,
  onUpdate,
}) => {
  const { t } = useTranslation()
  const [title, setTitle] = useState(url.metadata?.title || url.title || '')
  const [notes, setNotes] = useState(url.notes || '')
  const [tags, setTags] = useState<Tag[]>(url.tags || [])
  const [folderId, setFolderId] = useState<string | null>(
    url.folderId?._id || null
  )

  // 보안 관련 state 추가
  const [hasPassword, setHasPassword] = useState(
    !!url.hasPassword || !!url.security?.hasPassword
  )
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [hasExpiration, setHasExpiration] = useState(
    !!url.expiresAt || !!url.security?.expiresAt
  )
  const [expiresAt, setExpiresAt] = useState(() => {
    const existingDate = url.expiresAt || url.security?.expiresAt
    if (existingDate) {
      // ISO 문자열을 로컬 datetime-local 형식으로 변환
      const date = new Date(existingDate)
      return date.toISOString().slice(0, 16) // YYYY-MM-DDTHH:mm 형식
    }
    return ''
  })

  const [loading, setLoading] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const queryClient = useQueryClient()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError('')

    // 비밀번호 검증
    if (hasPassword && password) {
      if (password.length < 4) {
        setPasswordError(t('form.passwordTooShort'))
        return
      }
      if (password !== confirmPassword) {
        setPasswordError(t('form.passwordMismatch'))
        return
      }
    }

    setLoading(true)

    try {
      const updateData: any = {
        title,
        notes,
        tags: tags.map(tag => tag.name),
        folderId: folderId,
      }

      // 보안 관련 필드 추가
      if (hasPassword && password) {
        updateData.password = password
      } else if (!hasPassword) {
        updateData.password = null // 비밀번호 제거
      }

      if (hasExpiration && expiresAt) {
        updateData.expiresAt = new Date(expiresAt).toISOString()
      } else if (!hasExpiration) {
        updateData.expiresAt = null // 만료 날짜 제거
      }

      const data = await bookmarkApi.update(url._id, updateData)
      if (data.success) {
        if (data.data?.url) {
          onUpdate(data.data.url)
        }
        queryClient.invalidateQueries({ queryKey: ['bookmarked-urls'] })
        queryClient.invalidateQueries({ queryKey: ['tags'] })
        onClose()
      } else {
        setPasswordError(data.message || t('form.updateError'))
      }
    } catch (error) {
      setPasswordError(
        error instanceof Error ? error.message : t('form.updateError')
      )
      console.error('URL 업데이트 실패:', error)
      setPasswordError(t('form.updateFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={e => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-gray-800"
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('form.editLink')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.title')}
            </label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder={t('form.titlePlaceholder')}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.folder')}
            </label>
            <FolderSelect
              value={folderId}
              onChange={setFolderId}
              placeholder={t('form.folderPlaceholder')}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.tags')}
            </label>
            <TagInput
              value={tags}
              onChange={setTags}
              placeholder={t('form.tagsPlaceholder')}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('form.notes')}
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder={t('form.notesPlaceholder')}
            />
          </div>

          {/* 보안 설정 섹션 */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <h3 className="mb-4 flex items-center text-lg font-medium text-gray-900 dark:text-white">
              <LockClosedIcon className="mr-2 h-5 w-5" />
              {t('form.security')}
            </h3>

            {passwordError && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {passwordError}
                </p>
              </div>
            )}

            {/* 비밀번호 보호 */}
            <div className="mb-6">
              <div className="mb-3 flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={hasPassword}
                    onChange={e => {
                      setHasPassword(e.target.checked)
                      if (!e.target.checked) {
                        setPassword('')
                        setConfirmPassword('')
                        setPasswordError('')
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    {hasPassword ? (
                      <LockClosedIcon className="mr-1 h-4 w-4" />
                    ) : (
                      <LockOpenIcon className="mr-1 h-4 w-4" />
                    )}
                    {t('form.passwordProtectOn')}
                  </span>
                </label>
                {url.hasPassword && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('form.passwordSet')}
                  </span>
                )}
              </div>

              {hasPassword && (
                <div className="space-y-3 pl-6">
                  <div>
                    <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">
                      {t('form.newPassword')}
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="최소 4자 이상"
                      minLength={4}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">
                      {t('form.passwordConfirm')}
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="비밀번호 재입력"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* {t('form.expiry')} */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={hasExpiration}
                    onChange={e => {
                      setHasExpiration(e.target.checked)
                      if (!e.target.checked) {
                        setExpiresAt('')
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <ClockIcon className="mr-1 h-4 w-4" />
                    {t('form.expiry')}
                  </span>
                </label>
                {(url.expiresAt || url.security?.expiresAt) && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('form.currentValue')}{' '}
                    {formatDateTime(url.expiresAt || url.security?.expiresAt)}
                  </span>
                )}
              </div>

              {hasExpiration && (
                <div className="pl-6">
                  <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">
                    {t('form.expiryDateTime')}
                  </label>
                  <input
                    type="datetime-local"
                    value={expiresAt}
                    onChange={e => setExpiresAt(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('form.expiryHint')}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? t('form.saving') : t('form.save')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

export default EditUrlModal
